import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { useModals } from 'core/notifications';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

import { Loading } from 'uikit/loaders';
import { Typo } from 'uikit/content';

import getPublishModal from '../helpers/getPublishModal';
import getPublishToLocalesModal from '../helpers/getPublishToLocalesModal';

import useGlobalQueries from '../hooks/useGlobalQueries';

import SegmentedButton from './segmentedButton';
import VersioningToolbarLocaleSelect from './versioningToolbarLocaleSelect';

import styles from '../styles/versioningToolbar.module.scss';

const VersioningToolbar = ({
  getNewLocaleAxiosConfig = () => {},
  hasUnpublishedChanges = false,
  isArchived = false,
  isLoading = false,
  isLocaleSelectDisabled = false,
  isLocaleSelectOnly = false,
  isPublished = false,
  isPublishing = false,
  locale = null,
  locales = [],
  onApproveClick = () => {},
  onLocaleAdd = () => {},
  onLocaleChange = () => {},
  onPublishClick = () => {},
  onSubmitForReviewClick = () => {},
  onUnlockClick = () => {},
  publishData = {},
  publishedAt = null,
  status = null,
  updatedAt = null,
}) => {
  const { adminSettings } = useGlobalQueries();
  const isLoadingLocaleLabels = adminSettings.status !== 'success';
  const { addModal } = useModals();
  const { lang } = document.documentElement;
  const date = new Date(updatedAt).toLocaleString(lang, { dateStyle: 'short' });
  const isInReview = status === 'in_review';
  const isApproved = status === 'approved';
  const shouldShowLabel = hasUnpublishedChanges || isInReview || isApproved;

  const handlePublishClick = async () => {
    const isSingleLocalePublish = locales.length === 1;

    const { payload, type } = await addModal(
      isSingleLocalePublish
        ? getPublishModal(publishData)
        : getPublishToLocalesModal({
            handlePublishLocales: onPublishClick,
            locales,
            locale,
            publishedAt,
            isPublished,
            updatedAt,
            status,
            hasUnpublishedChanges,
          }),
    );

    if (type === 'submit') {
      onPublishClick(payload);
    }
  };

  const ACTIONS = {
    approve: { key: 'approve', onClick: onApproveClick },
    submitForReview: { key: 'submit-for-review', onClick: onSubmitForReviewClick },
    publish: { key: 'publish', onClick: handlePublishClick },
    unlock: { key: 'unlock', onClick: onUnlockClick },
  };

  const getPrimaryAction = () => {
    if (isInReview) return ACTIONS.approve;
    if (isApproved) return ACTIONS.publish;

    return ACTIONS.submitForReview;
  };

  const getSecondaryActions = () => {
    if (isInReview) return [ACTIONS.unlock, ACTIONS.publish];
    if (isApproved) return [ACTIONS.unlock];

    return [ACTIONS.approve, ACTIONS.publish];
  };

  const getStatusString = () => {
    if (isArchived) return 'archived';

    return isPublished ? 'published' : 'unpublished';
  };

  return (
    <div className={styles.element}>
      {isLoading || isLoadingLocaleLabels ? (
        <Loading className={styles.loader} />
      ) : (
        <>
          <div className={styles.column}>
            <VersioningToolbarLocaleSelect
              getNewLocaleAxiosConfig={getNewLocaleAxiosConfig}
              isDisabled={isLocaleSelectDisabled}
              locale={locale}
              locales={locales}
              onAdd={onLocaleAdd}
              onChange={onLocaleChange}
            />
            {!isLocaleSelectOnly && (
              <div className={styles.textContainer}>
                <Typo
                  className={classnames(styles.textContainer, styles.hasDot, {
                    [styles.isPublished]: isPublished,
                  })}
                  variant="s02"
                  weight="medium"
                >
                  {i18n.t(getStatusString())}
                </Typo>
                {(isPublished || isArchived) && (
                  <Typo color="secondary" variant="s02">
                    {new Date(isPublished ? publishedAt : updatedAt).toLocaleString(lang, {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </Typo>
                )}
              </div>
            )}
          </div>
          {!isLocaleSelectOnly && (
            <div className={styles.column}>
              {isPublishing ? (
                <div className={styles.textContainer}>
                  <Loading className={classnames(styles.loader, styles.isPublishing)} size={18} />
                  <Typo variant="s02" weight="medium">
                    {i18n.t('publishing')}
                  </Typo>
                </div>
              ) : (
                <>
                  {shouldShowLabel && (
                    <div className={styles.textContainer}>
                      {hasUnpublishedChanges && (
                        <>
                          <WarningAmberOutlined
                            className={styles.secondary}
                            sx={{ height: 18, width: 18 }}
                          />
                          <Typo color="secondary" variant="s02">
                            {i18n.t('unpublished-changes')}
                          </Typo>
                        </>
                      )}
                      {isInReview && (
                        <Typo color="secondary" variant="s02">
                          {i18n.t('submitted-for-review-date', { date })}
                        </Typo>
                      )}
                      {isApproved && (
                        <Typo color="secondary" variant="s02">
                          {i18n.t('approved-date', { date })}
                        </Typo>
                      )}
                    </div>
                  )}
                  <SegmentedButton
                    primaryAction={getPrimaryAction()}
                    secondaryActions={getSecondaryActions()}
                  />
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

VersioningToolbar.propTypes = {
  getNewLocaleAxiosConfig: PropTypes.func,
  hasUnpublishedChanges: PropTypes.bool,
  isArchived: PropTypes.bool,
  isLoading: PropTypes.bool,
  isLocaleSelectDisabled: PropTypes.bool,
  isLocaleSelectOnly: PropTypes.bool,
  isPublished: PropTypes.bool,
  isPublishing: PropTypes.bool,
  locale: PropTypes.string,
  locales: PropTypes.arrayOf(
    PropTypes.shape({
      has_unpublished_changes: PropTypes.bool,
      id: PropTypes.number,
      is_published: PropTypes.bool,
      locale: PropTypes.string,
      published_at: PropTypes.string,
      version_state_updated_at: PropTypes.string,
    }),
  ),
  onApproveClick: PropTypes.func,
  onLocaleAdd: PropTypes.func,
  onLocaleChange: PropTypes.func,
  onPublishClick: PropTypes.func,
  onSubmitForReviewClick: PropTypes.func,
  onUnlockClick: PropTypes.func,
  publishData: PropTypes.shape({}),
  publishedAt: PropTypes.string,
  status: PropTypes.oneOf(['approved', 'editing', 'in_review', 'in_translation', null]),
  updatedAt: PropTypes.string,
};

export default VersioningToolbar;
